"use client";

// Upload a large recipient list to the staging endpoint with bounded concurrency.
//
// Staging used to be a strictly sequential `for` loop: one request, await, next. A 2,000,000
// recipient campaign at 2,000 per request is 1,000 round trips, each going browser -> Next proxy
// -> gateway -> SQL. At a couple of hundred milliseconds apiece that is minutes of the operator
// sitting on the page waiting, with nothing else happening in between.
//
// The requests are independent, so they can overlap. A small worker pool keeps `concurrency`
// uploads in flight at once without flooding the gateway or the connection pool behind it.
//
// ORDERING: chunks may now be inserted out of order relative to the file. That is deliberate and
// safe — the ingest worker claims staged recipients by their own row id, and a campaign has no
// meaningful per-recipient ordering. Do not use this where order matters.
//
// FAILURE: the first error aborts the remaining work and is rethrown, matching the old loop's
// behaviour. Chunks already uploaded stay staged; the job is only started after this resolves,
// so a failure means the job is simply never started.
export async function stageInBatches<T>({
  items,
  batchSize,
  concurrency,
  upload,
  onProgress
}: {
  items: T[];
  batchSize: number;
  concurrency: number;
  upload: (chunk: T[]) => Promise<unknown>;
  onProgress?: (staged: number) => void;
}): Promise<void> {
  const total = items.length;
  if (total === 0) return;

  const chunkCount = Math.ceil(total / batchSize);
  const workers = Math.max(1, Math.min(concurrency, chunkCount));

  let nextChunk = 0;
  let staged = 0;
  let aborted = false;

  async function worker() {
    while (!aborted) {
      const index = nextChunk++;
      if (index >= chunkCount) return;

      // Sliced per chunk rather than pre-splitting the whole list, so a multi-million entry
      // array is never duplicated in memory.
      const chunk = items.slice(index * batchSize, (index + 1) * batchSize);

      try {
        await upload(chunk);
      } catch (error) {
        aborted = true;
        throw error;
      }

      staged += chunk.length;
      onProgress?.(staged);
    }
  }

  // Promise.all rejects on the first failure; the `aborted` flag stops the others promptly.
  await Promise.all(Array.from({ length: workers }, () => worker()));
}
